import { useCallback, useEffect, useMemo, useState } from 'react';
import { Col, Form, Row } from 'react-bootstrap';
import Multiselect, { MultiValue } from 'react-select';
import { Button } from '../Controls/Button';

/** Minimal shape for building facility options (e.g. ServerTransferFacilityLite). */
export interface ReportFacilityOption {
    facilityId: number;
    name?: string | null;
}

export type FacilitySelectOption = { label: string; value: number };

export function toReportFacilityOptions(
    facilities: ReportFacilityOption[] | undefined
): FacilitySelectOption[] {
    return (facilities ?? [])
        .filter((f) => f.facilityId != null)
        .map((f) => ({
            label: f.name || `Facility ${f.facilityId}`,
            value: f.facilityId,
        }));
}

/**
 * Multi-facility selection for reports. When `syncSelectedIds` is undefined, no sync effect runs
 * (use for controlled-only flows). When it is defined (including `[]`), selection is reset whenever
 * `syncSelectedIds` or `facilityOptions` change—pass a stable array reference from parent state.
 * `null` is treated like `[]` for syncing (no facilities selected).
 */
export function useReportFacilityMultiSelect(
    facilities: ReportFacilityOption[] | undefined,
    syncSelectedIds?: number[] | null
) {
    const facilityOptions = useMemo(
        () => toReportFacilityOptions(facilities),
        [facilities]
    );

    const [selectedOptions, setSelectedOptions] = useState<FacilitySelectOption[]>([]);

    useEffect(() => {
        if (syncSelectedIds === undefined) return;
        const ids = syncSelectedIds ?? [];
        setSelectedOptions(facilityOptions.filter((o) => ids.includes(o.value)));
    }, [syncSelectedIds, facilityOptions]);

    const selectedIds = useMemo(
        () => selectedOptions.map((o) => o.value),
        [selectedOptions]
    );

    const setSelectedIds = useCallback(
        (ids: number[]) => {
            setSelectedOptions(facilityOptions.filter((o) => ids.includes(o.value)));
        },
        [facilityOptions]
    );

    const onMultiChange = useCallback((opts: MultiValue<FacilitySelectOption>) => {
        setSelectedOptions(opts ? [...opts] : []);
    }, []);

    const selectAll = useCallback(
        () => setSelectedOptions(facilityOptions),
        [facilityOptions]
    );

    const clearAll = useCallback(() => setSelectedOptions([]), []);

    return {
        facilityOptions,
        selectedOptions,
        selectedIds,
        onMultiChange,
        selectAll,
        clearAll,
        setSelectedIds,
    };
}

export function ReportFacilityMultiSelectRow({
    facilityOptions,
    selectedOptions,
    onMultiChange,
    selectAll,
    clearAll,
    label = 'Facilities',
    className = 'mb-3',
}: {
    facilityOptions: FacilitySelectOption[];
    selectedOptions: FacilitySelectOption[];
    onMultiChange: (opts: MultiValue<FacilitySelectOption>) => void;
    selectAll: () => void;
    clearAll: () => void;
    label?: string;
    className?: string;
}) {
    return (
        <Row className={className}>
            <Col>
                <Form.Group>
                    <div className="d-flex align-items-center mb-2 flex-nowrap">
                        <Form.Label className="mb-0 me-3 flex-shrink-0">{label}</Form.Label>
                        {facilityOptions.length > 1 && (
                            <div className="flex-shrink-0 me-2">
                                <Button
                                    type="button"
                                    size="sm"
                                    variant="outline-primary"
                                    className="me-1"
                                    onClick={selectAll}
                                >
                                    Select All
                                </Button>
                                <Button
                                    type="button"
                                    size="sm"
                                    variant="outline-secondary"
                                    onClick={clearAll}
                                >
                                    None
                                </Button>
                            </div>
                        )}
                        <div className="flex-grow-1 min-w-0">
                            <Multiselect
                                onChange={onMultiChange}
                                value={selectedOptions}
                                isMulti
                                options={facilityOptions}
                                placeholder="Select facilities..."
                                isClearable={false}
                            />
                        </div>
                    </div>
                </Form.Group>
            </Col>
        </Row>
    );
}
